import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from 'primereact/button'
import { StepperRefAttributes } from 'primereact/stepper'

type Props = {
  stepperRef: React.MutableRefObject<StepperRefAttributes | null>
  hasError?: boolean
  isFirstStep?: boolean
  isLastStep?: boolean
  isVerficationStep?: boolean
  prevButtonCallback?: () => Promise<boolean | void>
  nextButtonCallback?: () => Promise<boolean | void>
  sendButtonCallback?: () => void
  deleteEinvoice?: () => void
  isRequestRunning?: boolean
  isPrevButtonDisabled?: boolean
  isNextButtonDisabled?: boolean
}

export const Footer: React.FC<Props> = ({
  stepperRef,
  hasError = false,
  isFirstStep = false,
  isLastStep = false,
  isVerficationStep = false,
  prevButtonCallback,
  nextButtonCallback,
  sendButtonCallback,
  deleteEinvoice,
  isRequestRunning = false,
  isPrevButtonDisabled = false,
  isNextButtonDisabled = false,
}) => {
  const { t } = useTranslation('eInvoiceDetails')
  const navigate = useNavigate()

  const handlePrev = async () => {
    if (prevButtonCallback) {
      const success = await prevButtonCallback()

      // Only navigate back if successful (or undefined/true)
      if (success !== false) {
        stepperRef.current?.prevCallback()
      }
    } else {
      stepperRef.current?.prevCallback()
    }
  }

  const handleNext = async () => {
    if (nextButtonCallback) {
      const success = await nextButtonCallback()

      // Only navigate forward if successful (or undefined/true)
      if (success !== false) {
        stepperRef.current?.nextCallback()
      }
    } else {
      stepperRef.current?.nextCallback()
    }
  }

  const handleCancel = async () => {
    deleteEinvoice && (await deleteEinvoice())
    navigate('/e-invoices')
  }

  const prevButton = (
    <Button
      label={t('stepper.footer.back.label')}
      icon="pi pi-arrow-left"
      onClick={handlePrev}
      text
      loading={isRequestRunning}
      disabled={isPrevButtonDisabled}
    />
  )

  const nextButton = (
    <Button
      label={t('stepper.footer.forward.label')}
      icon="pi pi-arrow-right"
      iconPos="right"
      onClick={handleNext}
      className="ml-auto"
      text
      loading={isRequestRunning}
      disabled={isNextButtonDisabled}
    />
  )

  const sendButton = (
    <Button
      label={t('stepper.footer.send.label')}
      icon="pi pi-send"
      iconPos="right"
      onClick={sendButtonCallback}
      loading={isRequestRunning}
    />
  )

  const errorButton = (
    <Button
      label={t('stepper.footer.restart.label')}
      onClick={handleCancel}
      loading={isRequestRunning}
    />
  )

  if (hasError && !isVerficationStep)
    return <div className="flex justify-content-center mt-4">{errorButton}</div>

  if (hasError && isVerficationStep) {
    return (
      <div className="flex justify-content-between mt-4">
        {prevButton} {errorButton}
      </div>
    )
  }

  return (
    <div className="flex justify-content-between mt-4">
      {!isFirstStep && prevButton}

      {!isLastStep && nextButton}

      {isLastStep && sendButton}
    </div>
  )
}
